// Daten der Zähler-Detailseite: der Zähler (mit Objekt/Mieter) und alle seine Ablesungen, neueste zuerst.

import { useCallback, useEffect, useState } from 'react';
import { supabase } from '../supabase/client';
import type { ZaehlerRoh } from './liste';
import type { AblesungMitFoto } from './zaehlerApi';

export type ZaehlerDaten = Omit<ZaehlerRoh, 'ablesungen'>;

export interface AblesungZeile extends AblesungMitFoto {
  notizen: string | null;
  erfasser: string | null;
}

interface AblesungRoh {
  id: string; datum: string; wert: number | string; verbrauch: number | string | null; foto_url: string | null;
  notizen: string | null; created_at: string | null; erfasser: { full_name: string | null } | null;
}

export type ZaehlerDetailZustand =
  | { status: 'laedt' } | { status: 'fehler'; meldung: string } | { status: 'fehlt' }
  | { status: 'ok'; zaehler: ZaehlerDaten; ablesungen: AblesungZeile[] };

const zahl = (v: number | string | null): number | null => (v === null || v === '' ? null : Number(v));

export async function ladeZaehlerDetail(id: string): Promise<{ zaehler: ZaehlerDaten; ablesungen: AblesungZeile[] } | null> {
  const [z, a] = await Promise.all([
    supabase.from('zaehler').select('*, objekt:objekte(id, bezeichnung, strasse, hausnummer, objektnummer), mieter:mieter(id, vorname, nachname)')
      .eq('id', id).maybeSingle(),
    supabase.from('ablesungen').select('id, datum, wert, verbrauch, foto_url, notizen, created_at, erfasser:profiles!ablesungen_erfasst_von_fkey(full_name)')
      .eq('zaehler_id', id).order('datum', { ascending: false }).order('created_at', { ascending: false }),
  ]);
  if (z.error) throw new Error(z.error.message);
  if (a.error) throw new Error(a.error.message);
  if (!z.data) return null;
  const ablesungen = ((a.data ?? []) as unknown as AblesungRoh[]).map(r => ({
    id: r.id, datum: r.datum.slice(0, 10), wert: Number(r.wert), verbrauch: zahl(r.verbrauch), foto_url: r.foto_url,
    notizen: r.notizen, created_at: r.created_at, erfasser: r.erfasser?.full_name ?? null,
  }));
  return { zaehler: z.data as unknown as ZaehlerDaten, ablesungen };
}

export function useZaehlerDetail(id: string): { zustand: ZaehlerDetailZustand; neuLaden: () => void } {
  const [zustand, setZustand] = useState<ZaehlerDetailZustand>({ status: 'laedt' });
  const [runde, setRunde] = useState(0);
  useEffect(() => {
    let aktiv = true;
    ladeZaehlerDetail(id)
      .then(d => { if (aktiv) setZustand(d ? { status: 'ok', ...d } : { status: 'fehlt' }); })
      .catch((e: unknown) => { if (aktiv) setZustand({ status: 'fehler', meldung: e instanceof Error ? e.message : 'Unbekannter Fehler' }); });
    return () => { aktiv = false; };
  }, [id, runde]);
  const neuLaden = useCallback(() => setRunde(r => r + 1), []);
  return { zustand, neuLaden };
}
